import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Button } from "@legends/ui";
import type { RollItem } from "./api";

// The pre/post-roll looping player (ADR 0052, hardened ADR 0110). Given a resolved
// playlist it fills its (positioned) parent and loops forever: each image is shown
// for its dwell (default 8s), each video plays fully (muted autoplay, with an
// Enable-audio gesture), then it advances to the next item and wraps back to the
// top. Reuses the AdSurface full-frame treatment (contain for slates, cover for
// video).
//
// Robustness (ADR 0110): the advance is driven by a content signature + position,
// not by the `items` array *reference*, so a caller that re-creates the array each
// render can't strand the loop on item 0 or leak/reset the dwell timer; and a video
// that fails to load advances past (onError) instead of wedging the loop.

const fill: CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%" };
const DEFAULT_DWELL_MS = 8000;

export function RollPlayer({ items, ariaLabel }: { items: RollItem[]; ariaLabel: string }) {
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);

  const count = items.length;
  // A content signature: the loop restarts from the top only when the playlist's
  // *contents* change, not merely its array identity (so an unmemoized prop is safe).
  const sig = items.map((it) => it.id).join("|");

  useEffect(() => {
    setIndex(0);
  }, [sig]);

  const next = useCallback(() => {
    setIndex((i) => (count > 0 ? (i + 1) % count : 0));
  }, [count]);

  // Clamp the position so a shrunk playlist (before the reset effect runs) and a
  // monotonic index both resolve in range.
  const current = count > 0 ? index % count : 0;
  const item = count > 0 ? items[current] : undefined;

  const isImage = item?.kind === "image";
  const dwellMs = item && item.durationMs && item.durationMs > 0 ? item.durationMs : DEFAULT_DWELL_MS;

  // Images advance on a dwell timer; videos advance on `ended` (or loop natively when
  // they're the only item). Keyed on primitives so array-reference churn never resets
  // the timer mid-dwell, and always cleaned up (no timer leak).
  useEffect(() => {
    if (!isImage) return;
    const t = setTimeout(next, dwellMs);
    return () => clearTimeout(t);
  }, [sig, current, isImage, dwellMs, next]);

  if (!item) return null;

  return (
    <div style={{ ...fill, background: "#000" }} aria-label={ariaLabel}>
      {item.kind === "video" ? (
        <video
          key={`${current}-${item.id}`}
          src={item.url}
          autoPlay
          muted={muted}
          playsInline
          loop={count === 1}
          onEnded={count > 1 ? next : undefined}
          onError={count > 1 ? next : undefined}
          aria-label={`${ariaLabel}: ${item.title || "clip"}`}
          style={{ ...fill, objectFit: "cover", background: "#000" }}
        />
      ) : (
        <img
          key={`${current}-${item.id}`}
          src={item.url}
          alt={`${ariaLabel}: ${item.title || "slate"}`}
          style={{ ...fill, objectFit: "contain", background: "#000" }}
        />
      )}
      {item.kind === "video" && muted && (
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}>
          <Button size="sm" variant="accent" aria-label="Enable audio" onClick={() => setMuted(false)}>
            Enable audio
          </Button>
        </div>
      )}
    </div>
  );
}
